import { useRef, useState } from "react";
import {
  COLUMN_NAMES,
  IQuestionsButtonOptions,
  QuestionType,
} from "../../Template/Template";
import { useDrag, useDrop } from "react-dnd";
import {
  getLocalTranslation,
  useComponentVisibles,
} from "../../../../utils/functions";
import EditSvg from "../../../../assets/img/edit.svg";
import LanguageIcon from "../../../../assets/img/language.svg";
import SnackChip from "../../../../components/SnackChip/SnackChip";
import getClasses from "../../../../utils/getClasses";
import { useTranslation } from "react-i18next";
import { ANSWER_TYPE, IQuestion } from "../../../../models/SurveyModels";
require("./Questions.scss");
export interface MovableItemType extends QuestionType {
  index: number;
  currentColumnName?: string;
  moveCardHandler?: Function;
  setItems?: Function;
  onChange?: (value: IQuestion[]) => void;
  noDrag?: boolean;
  buttonsOption?: Array<IQuestionsButtonOptions>;
}

export const Question = ({
  answerMode,
  column,
  id,
  translations,
  type,
  actor,
  categories,
  createdAt,
  enabled,
  templates,
  self,
  isIssueLinked,
  index,
  noDrag,
  currentColumnName,
  buttonsOption,
  moveCardHandler,
  setItems,
  onChange,
}: MovableItemType) => {
  const changeItemColumn = (
    currentItem: MovableItemType,
    columnName: string
  ) => {
    if (setItems) {
      setItems((prevState: QuestionType[]) => {
        return prevState.map((QUESTION: QuestionType, index) => {
          return {
            ...QUESTION,
            column:
              QUESTION.id === currentItem.id ? columnName : QUESTION.column,
          };
        });
      });
    }
  };
  const ref = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop({
    accept: "Question",
    hover(item: MovableItemType, monitor) {
      if (!ref.current) return;

      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY =
        clientOffset && clientOffset.y - hoverBoundingRect.top;
      if (
        dragIndex < hoverIndex &&
        hoverClientY &&
        hoverClientY < hoverMiddleY
      ) {
        return;
      }
      if (
        dragIndex > hoverIndex &&
        hoverClientY &&
        hoverClientY > hoverMiddleY
      ) {
        return;
      }
      moveCardHandler && moveCardHandler(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    item: {
      index,
      currentColumnName,
      answerMode,
      column,
      id,
      translations,
      type,
      actor,
      categories,
      createdAt,
      enabled,
      templates,
      isIssueLinked,
      self,
      buttonsOption,
    },
    type: "Question",
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult() as MovableItemType;
      if (dropResult) {
        const { id } = dropResult;
        const { QUESTION, TEMPLATE } = COLUMN_NAMES;
        switch (id) {
          case QUESTION:
            changeItemColumn(item, QUESTION);
            break;
          case TEMPLATE:
            changeItemColumn(item, TEMPLATE);
            break;
          default:
            break;
        }
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const { t } = useTranslation();
  const opacity = isDragging ? 0.4 : 1;
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const handleRefAssignment =
    (index: number) => (ref: HTMLDivElement | null) => {
      refs.current[index] = ref;
    };
  const { isComponentVisible, setComponentVisible } = useComponentVisibles(
    false,
    refs
  );
  const optionsButton = (indexData: number, item: QuestionType) => {
    const handleOptionClick = (optionIndex: number) => {
      const option = buttonsOption && buttonsOption[indexData][optionIndex];
      if (option && option.onClick) {
        setComponentVisible(indexData);
        option.onClick(item);
      }
    };
    return (
      <div
        key={indexData}
        className="table-options-menu"
        ref={handleRefAssignment(indexData)}
      >
        <div>
          <div
            className="wrapper-options"
            onClick={() => {
              setComponentVisible(indexData);
            }}
          >
            <img src={EditSvg} alt="edit"></img>
          </div>
          <div
            className={`container-options ${
              isComponentVisible[indexData] ? "open" : ""
            }`}
          >
            {buttonsOption![indexData].map(
              ({ label, onClick, colorTheme, icon, isLoading }, index) => (
                <div className="item-options " key={index}>
                  <div
                    key={index}
                    onClick={() => {
                      handleOptionClick(index);
                    }}
                    className={`menu-item menu-item-${colorTheme}`}
                  >
                    <span
                      className={`icon  icon-button-table color-${colorTheme}`}
                    >
                      {icon}
                    </span>
                    <span
                      className={`capitalize-first-letter color-${colorTheme}`}
                    >
                      {label}
                    </span>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  !noDrag && drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`question-container ${getClasses({
        isDragging: isDragging,
      })}  ${getClasses({ pointer: noDrag, drag: !noDrag })} `}
      style={{ opacity }}
    >
      <div className="question-wrapper">
        <div className="wrapper-info">
          {!noDrag && currentColumnName === "template" && (
            <div className="drag-point">
              <i className={`icon icon-drag2`}></i>
            </div>
          )}
          <div className="info">
            <div className="wrapper-chip">
              <div>
                <SnackChip
                  label={categories[0]?.name}
                  borderRadius="sm"
                  color={categories[0]?.color}
                  backgroundColor={categories[0]?.background}
                  options={{ colorTextNeutral: true }}
                  iconLeft={categories[0]?.icon}
                  theme="success"
                ></SnackChip>
              </div>
              {isIssueLinked && (
                <div>
                  <SnackChip
                    label=""
                    borderRadius="sm"
                    iconLeft={"icon-issue"}
                    theme="is-linked-issue"
                  />
                </div>
              )}
            </div>
            <div className="question-text">
              {getLocalTranslation(translations)?.text}
            </div>
            <div className="footer-questions">
              {answerMode && (
                <div className="answer-mode-wrapper">
                  <i
                    className={` icon ${
                      answerMode === ANSWER_TYPE.FREE_TEXT
                        ? "icon-file"
                        : answerMode === ANSWER_TYPE.LIKERT
                        ? "icon-star"
                        : answerMode === ANSWER_TYPE.DAYS && "icon-planner"
                    }`}
                  ></i>
                  <span className="text-footer-questions">
                    {t(`selectOptions.${answerMode}`)}
                  </span>
                </div>
              )}
              <div className="language-wrapper">
                <img
                  className="language-image"
                  src={LanguageIcon}
                  alt="edit"
                ></img>
                <span className="text-footer-questions">
                  {translations.map(({ language }, index) => {
                    return `${t(`home.questions.language.${language}`)}${
                      index + 1 < translations.length ? "," : ""
                    } `;
                  })}
                </span>
              </div>
            </div>
            {!noDrag &&
              column !== COLUMN_NAMES.TEMPLATE &&
              templates?.length > 0 && (
                <div className="footer-questions sub">
                  <span className="text-used"> {t("home.questions.used")}</span>
                  <span className="text-footer-questions">
                    {templates?.map((template, index) => {
                      return `${template}${
                        index + 1 < translations?.length ? "," : ""
                      } `;
                    })}
                  </span>
                </div>
              )}
          </div>
        </div>
        <>
          {buttonsOption &&
            optionsButton(index, {
              column,
              answerMode,
              categories,
              createdAt,
              enabled,
              isIssueLinked,
              templates,
              id,
              translations,
              type,
              actor,
              self,
            })}
        </>
      </div>
    </div>
  );
};
